import { component } from 'picoapp'
import { updateItemById } from '@/lib/cart.js'
import { formatMoney } from '@/lib/currency.js'
import app from '@/app.js'

class Cart {
  constructor(node, ctx) {
    // bindings
    this.onCloseClick = this.onCloseClick.bind(this)
    this.onOnItemClick = this.onOnItemClick.bind(this)
    this.onCartUpdated = this.onCartUpdated.bind(this)

    // selectors
    this._node = node
    this._items = document.getElementById('custom-cart-items')
    this._error = document.getElementById('custom-cart-error')
    this._subtotal = document.getElementById('custom-cart-subtotal')
    this._close = document.getElementById('cart-close-btn')
    this._continue_shopping = document.getElementById('continue-shopping')
    this._checkout_btn = document.getElementById('checkout-btn')

    this._progress_bar = this._node.querySelector('.progress-bar')
    this._price_to_go = this._node.querySelector('.to-go')

    let data_fsp = this._price_to_go.getAttribute('data-fsp')
    this._fsp = data_fsp !== '' ? parseInt(data_fsp, 10) : 0
    this._ptg = this._fsp

    // privates
    this._ctx = ctx
    this._locked = false

    // events
    this._items.addEventListener('click', this.onOnItemClick, false)
    this._ctx.on('cart:updated', this.onCartUpdated)
    this._close.addEventListener('click', this.onCloseClick, false)
    this._continue_shopping.addEventListener('click', this.onCloseClick, false)

    // render
    this.render(this._ctx.getState().cart)

    // unmount
    this.unmount.bind(this);
  }

  /**
   * Handle close click.
   */
  onCloseClick() {
    this.showCartError('')
    this._ctx.emit('cart:toggle', (state) => {
      return {
        cartOpen: false
      }
    })
  }

  /**
   * Handle item click.
   * Delegation for decrease, increase and remove.
   */
  onOnItemClick(e) {
    if (this._locked) return
    else this._locked = true

    this.showCartError('')


    let el = e.target,
        cl = el.classList

    if (cl.contains('decrease-item') || cl.contains('increase-item') || cl.contains('remove-item')) {
      let id = el.parentNode.parentNode.parentNode.parentNode.getAttribute('data-item'),
          quantity = parseInt(el.parentNode.parentNode.parentNode.parentNode.getAttribute('data-quantity'), 10)

      if (cl.contains('decrease-item') || cl.contains('increase-item')) {
        quantity = cl.contains('decrease-item') ? quantity - 1 : quantity + 1
      } else {
        quantity = 0
      }

      Promise.all([
        updateItemById(id, quantity)
      ]).then(([{item, cart}]) => {
        this._locked = false
      }).catch((error) => {
        this.showCartError(error)
        this._locked = false
      })
    }
  }

  /**
   * Show a cart error.
   */
  showCartError(error) {
    if (error === '') this._error.style.display = 'none'
    else this._error.style.display = 'block'

    this._error.innerText = error
  }

  /**
   * Render an item.
   */
  renderItem(item) {
    let price = formatMoney(item.price).replace(/(\.00)/gi, ''),
        selling_plan = "";

    if (item.selling_plan_allocation) {
      selling_plan = `<p class='fs-12'>${item.selling_plan_allocation.selling_plan.name}</p>`
      this._checkout_btn.href = "/checkout?discount=Subscription"
    }

    return `
      <div class="cart-item" data-item="${item.key}" data-quantity="${item.quantity}">
        <div class="cart-item-col">
          <a class="thumb" href="${item.url}">
            <img src="${item.image.replace(/(\.gif|\.jpg|\.png)/i, '_300x$1')}">
          </a>
        </div>
        <div class="cart-item-col">
          <div class="sub-col title-col">
            <p class="lg name"><strong>${item.title}</strong></p>
            ${selling_plan}
          </div>
          <div class="sub-col meta-col">
            <p class="mobile-price fs-12">${price}</p>
          </div>
          <div class="sub-col qty-col">
            <div class="quantity-box ash"><strong>${item.quantity}</strong><span class="decrease-item"></span><span class="increase-item"></span></div>
          </div>
        </div>
        <div class="cart-item-col">
          <div class="sub-col">
            <p class="lg total-item-price"><strong>${formatMoney(item.line_price).replace(/(\.00)/gi, '')}</strong></p>
          </div>
        </div>
      </div>
    `
  }

  /**
   * Re-render.
   */
  render(cart) {
    this._checkout_btn.href = "/checkout"

    let has_recharge = false,
        items = cart.items.map((item) => {
          if (item.properties && item.properties._subscription) {
            has_recharge = true
          }
          return this.renderItem(item)
        })

    if (items.length > 0 || this._fsp === 0) {
      this._node.classList.remove('empty-cart')

      let total = cart.total_price / 100,
          diff = Math.round((this._fsp - total) * 100) / 100,
          percent = (total / this._fsp) * 100

      if (has_recharge) {
        this._price_to_go.innerHTML = `<strong>You qualify for free shipping!</strong>`
        this._progress_bar.style.width = '100%'
      } else {
        if (diff > 0) {
          this._price_to_go.innerHTML = `<strong>You're only ${formatMoney(diff * 100).replace(/\.00/g, '')} away from free shipping!</strong>`
          this._progress_bar.style.width = `${percent}%`
        } else {
          this._price_to_go.innerHTML = `<strong>You qualify for free shipping!</strong>`
          this._progress_bar.style.width = '100%'
        }
      }
    } else {
      this._node.classList.add('empty-cart')
      this._ptg = this._fsp
      this._price_to_go.innerHTML = `<strong>Spend $${this._fsp} to get free shipping!</strong>`
      this._progress_bar.style.width = '0%'
    }

    this._items.innerHTML = items.join('')
    this._subtotal.innerText = formatMoney(cart.total_price).replace(/\.00/g, '')
  }

  /**
   * Handle cart updated.
   */
  onCartUpdated(state) {
    this.showCartError('')
    this.render(state.cart)
  }

  /**
   * Handle unmount.
   */
  unmount() {
    this._items.removeEventListener('click', this.onOnItemClick, false)
    this._ctx.on('cart:updated', this.onCartUpdated)
    this._close.removeEventListener('click', this.onCloseClick, false)
    this._continue_shopping.removeEventListener('click', this.onCloseClick, false)
  }
}

export default component((node, ctx) => new Cart(node, ctx))
